import { MockedResponse } from '@apollo/client/testing';
import type { FetchResult } from '@apollo/client';
import { ApolloError } from '@apollo/client';
import { SavedItemsColorOptionFragment, SavedItemsSavedItemFragment, SavedItemsShopProductVariantFragment } from '../../utils/hooks/getSavedItems';
import { DestroySavedItemDocument, type DestroySavedItemMutation } from '../../utils/hooks/destroySavedItem';
import { MOCK_CLIENT_ID } from '../../mocks';
import { GetSavedItemsOutfitsDocument, type GetSavedItemsOutfitsQuery } from '../../utils/hooks/getSavedItemsOutfits';
import { GetSimilarItemsDocument, type GetSimilarItemsQuery } from '../../utils/hooks/getSimilarItems';
type Item = {
  id: string;
  name: string;
};
type Outfit = {
  id: string;
};
type ResultConfig = {
  isAvailable?: SavedItemsSavedItemFragment['isAvailable'];
  items: Item[];
  inventory?: SavedItemsShopProductVariantFragment['inventory'];
  page?: number;
};
type SavedItemsOutfitsMockConfig = {
  req: OutfitsRequestConfig;
  res: OutfitsResultConfig;
};
type OutfitsRequestConfig = {
  numOutfits: number;
};
type OutfitsResultConfig = {
  outfits: Outfit[];
};
const savedItems = [{
  id: '123',
  name: 'shirt'
}, {
  id: '456',
  name: 'pants'
}];
const savedItemsList = [...Array(12).keys()].map(i => ({
  id: `product-${i}`,
  name: `pants-${i}`
}));
const savedItemsOutfits = [{
  id: 'bar'
}, {
  id: 'baz'
}];
const pageTwoItems = [{
  id: '678',
  name: 'hoodie'
}, {
  id: '901',
  name: 'jeans'
}];
export const getSavedItemsResult = ({
  items,
  isAvailable = true,
  inventory = {
    availability: 'AVAILABLE_STOCK',
    quantityAvailable: 50,
    stockMessage: {
      heading: null
    }
  },
  page = 1
}: ResultConfig) => ({
  data: {
    client: {
      __typename: 'Client' as const,
      id: MOCK_CLIENT_ID,
      savedItemsCollection: {
        __typename: 'SavedItemsCollection' as const,
        anyAvailable: items.length > 0,
        savedItemsByPage: {
          __typename: 'SavedItemPageConnection' as const,
          pageCount: page,
          edges: items.map(({
            id,
            name
          }) => ({
            __typename: 'SavedItemPageEdge' as const,
            node: {
              __typename: 'SavedItem' as const,
              skuId: id,
              isAvailable,
              productEventsContext: {
                source: 'categories'
              },
              variant: {
                __typename: 'ShopProductVariant' as const,
                id,
                sku: id,
                url: 'https://www.stitchfix.com/product/All-Saints-Lilliana-Kettu-Dress/V7P8MWMWP?sku-id=2257683&color=Gold&size=4',
                inventory,
                mayHaveOutfits: true,
                saved: true,
                color: {
                  __typename: 'Color' as const,
                  name: 'Purple',
                  uniqueColorId: '101800488'
                },
                size: {
                  __typename: 'Size' as const,
                  name: '2'
                },
                inseam: {
                  __typename: 'Inseam' as const,
                  name: '27'
                },
                price: {
                  __typename: 'Money' as const,
                  currency: 'USD' as const,
                  value: 5800
                },
                priceMarkdown: {
                  __typename: 'PriceMarkdown' as const,
                  fullPrice: {
                    __typename: 'Money' as const,
                    value: 6800,
                    currency: 'USD' as const
                  }
                },
                promoDiscountOffer: null,
                image: {
                  __typename: 'ShopMedia' as const,
                  id: `shop-media-${id}`,
                  altText: 'Tarek High Rise Straight Leg Jean - Purple - Flat Front',
                  baseUrl: 'https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/',
                  mediaPath: '/v1655250736/pswd5hadvs7uy7c01de3.jpg'
                },
                shopProduct: {
                  __typename: 'ShopProduct' as const,
                  id: `282896-${id}`,
                  name,
                  colorOptions: [{
                    __typename: 'ColorOption' as const,
                    name: 'Purple',
                    swatch: {
                      __typename: 'ColorSwatch' as const,
                      hex: '#956a67'
                    },
                    inventory: {
                      __typename: 'ShopProductColorInventory' as const,
                      availability: 'LIMITED_STOCK' as const
                    },
                    uniqueColorId: '101800488'
                  }, {
                    __typename: 'ColorOption' as const,
                    name: 'Purple',
                    swatch: {
                      __typename: 'ColorSwatch' as const,
                      hex: '#c08899'
                    },
                    inventory: {
                      __typename: 'ShopProductColorInventory' as const,
                      availability: 'LIMITED_STOCK' as const
                    },
                    uniqueColorId: '101800479'
                  }, {
                    __typename: 'ColorOption' as const,
                    name: 'Red',
                    swatch: {
                      __typename: 'ColorSwatch' as const,
                      hex: '#bd4d3a'
                    },
                    inventory: {
                      __typename: 'ShopProductColorInventory' as const,
                      availability: 'LIMITED_STOCK' as const,
                      quantityAvailable: 33
                    },
                    uniqueColorId: '101800489'
                  }],
                  brand: {
                    __typename: 'Brand' as const,
                    name: 'Just USA'
                  }
                }
              }
            }
          }))
        }
      }
    }
  }
});
export const getSavedItemsOutfitsResult = ({
  outfits = [{
    id: 'bar'
  }]
}): {
  data: GetSavedItemsOutfitsQuery & {
    client: NonNullable<GetSavedItemsOutfitsQuery['client']>;
  };
} => ({
  data: {
    client: {
      __typename: 'Client' as const,
      savedItemsCollection: {
        __typename: 'SavedItemsCollection' as const,
        outfits: {
          __typename: 'OutfitConnection' as const,
          nodes: outfits.map(({
            id
          }) => ({
            __typename: 'Outfit' as const,
            id,
            items: [{
              __typename: 'OutfitItem' as const,
              sticker: {
                __typename: 'ModeStickerLabel' as const,
                label: 'New Arrival',
                theme: 'CITRUS' as const
              },
              laydown: {
                __typename: 'OutfitItemLaydown' as const,
                maxHeight: 0.7,
                maxWidth: 0.2,
                x: 0.5,
                y: 0.5,
                xAnchor: 'center',
                yAnchor: 'center'
              },
              shopProductVariant: {
                __typename: 'ShopProductVariant' as const,
                sku: '2464369',
                url: 'https://www.stitchfix.com/product/All-Saints-Lilliana-Kettu-Dress/V7P8MWMWP?sku-id=2257683&color=Gold&size=4',
                image: {
                  __typename: 'ShopMedia' as const,
                  id: 'shop-media-2464369',
                  altText: 'Sabrina High Rise Slim Straight Jeans - Indigo - Flat Front',
                  imageUrl: 'https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/c_scale,h_500/e_trim:9/f_auto,q_auto/e_replace_color:f2f3f4:300:ffffff/v1655945007/pqwzpi0eqhikvz8ld2yl.jpg'
                }
              }
            }]
          }))
        }
      }
    }
  }
});
export const getSavedItemsSuccessMock = getSavedItemsResult({
  items: savedItems
});
export const getSavedItemsBrowserSuccessMock = getSavedItemsResult({
  items: savedItemsList,
  page: 2
});
export const getSavedItemsOutfitsBrowserSuccessMock = getSavedItemsOutfitsResult({
  outfits: savedItemsOutfits
});
export const getSavedItemsEmptyMock = getSavedItemsResult({
  items: []
});
export const getSavedItemsPageZeroMock = getSavedItemsResult({
  items: [],
  page: 0
});
export const getSavedItemsOutOfStockMock = getSavedItemsResult({
  items: savedItems,
  isAvailable: false,
  inventory: {
    availability: 'OUT_OF_STOCK',
    quantityAvailable: 0,
    stockMessage: {
      heading: 'Out of stock'
    }
  }
});
export const getSavedItemsPageTwoMock = getSavedItemsResult({
  items: pageTwoItems,
  page: 2
});
export const getSavedItemsLimitedStockMock = getSavedItemsResult({
  items: savedItems,
  inventory: {
    availability: 'LIMITED_STOCK',
    quantityAvailable: 5,
    stockMessage: {
      heading: 'Only 5 left in stock'
    }
  }
});
export const colorOptions: SavedItemsColorOptionFragment[] = [{
  inventory: {
    availability: 'LIMITED_STOCK'
  },
  name: 'Teal Green',
  swatch: {
    hex: '#84a9b5'
  },
  uniqueColorId: '101586104'
}, {
  inventory: {
    availability: 'OUT_OF_STOCK'
  },
  name: 'Blue',
  swatch: {
    hex: '#8695b8'
  },
  uniqueColorId: '101219060'
}];
export const shopProductVariant: SavedItemsShopProductVariantFragment = {
  __typename: 'ShopProductVariant',
  id: '2188405',
  sku: '2188405',
  url: 'https://www.stitchfix.com/product/All-Saints-Lilliana-Kettu-Dress/V7P8MWMWP?sku-id=2257683&color=Gold&size=4',
  inventory: {
    __typename: 'ShopInventory',
    availability: 'LIMITED_STOCK',
    quantityAvailable: 2,
    stockMessage: {
      heading: 'Only 2 left in stock'
    }
  },
  color: {
    __typename: 'Color',
    name: 'Light Green',
    uniqueColorId: '12345678'
  },
  inseam: null,
  price: {
    __typename: 'Money',
    currency: 'USD',
    value: 1497
  },
  priceMarkdown: {
    __typename: 'PriceMarkdown',
    fullPrice: {
      __typename: 'Money',
      value: 2500,
      currency: 'USD'
    }
  },
  promoDiscountOffer: null,
  image: {
    __typename: 'ShopMedia',
    id: 'shop-media-2188405',
    altText: 'Classic Crush Dye Tee - Light Green - Flat Front',
    baseUrl: 'https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/',
    mediaPath: '/v1650029659/aatkak9bg5eullnaintj.jpg'
  },
  saved: true,
  mayHaveOutfits: true,
  size: {
    __typename: 'Size',
    name: 'M'
  },
  shopProduct: {
    __typename: 'ShopProduct',
    id: '283076-2188405',
    name: 'Classic Crush Dye Tee',
    brand: {
      __typename: 'Brand',
      name: 'Champion'
    },
    colorOptions: [{
      __typename: 'ColorOption',
      inventory: {
        __typename: 'ShopProductColorInventory',
        availability: 'OUT_OF_STOCK'
      },
      name: 'Teal Green',
      swatch: {
        __typename: 'ColorSwatch',
        hex: '#84a9b5'
      },
      uniqueColorId: '101586104'
    }]
  }
};
export const getDestroySaveItemMock = (id = ''): MockedResponse<DestroySavedItemMutation> => ({
  request: {
    query: DestroySavedItemDocument,
    variables: {
      id
    }
  },
  result: {
    data: {
      savedItemDestroy: {
        __typename: 'SavedItemDestroyPayload',
        result: {
          __typename: 'SavedItemDestroyResult',
          sku: {
            __typename: 'SavedItemSku',
            id
          }
        }
      }
    }
  } as FetchResult<DestroySavedItemMutation>
});
export const getSavedItemsOutfitsMock = ({
  req: {
    numOutfits = 12
  },
  res
}: SavedItemsOutfitsMockConfig): MockedResponse<GetSavedItemsOutfitsQuery> => ({
  request: {
    query: GetSavedItemsOutfitsDocument,
    variables: {
      numOutfits
    }
  },
  result: getSavedItemsOutfitsResult(res) as FetchResult<GetSavedItemsOutfitsQuery>
});
export const getSavedItemsOutfitsMswMock = getSavedItemsOutfitsResult({
  outfits: savedItemsOutfits
});
export const getDestroySaveItemErrorMock = (id = ''): MockedResponse<DestroySavedItemMutation> => ({
  request: {
    query: DestroySavedItemDocument,
    variables: {
      id
    }
  },
  error: new ApolloError({
    errorMessage: 'Something went wrong. Please try again.'
  })
});
export const getFeatureFlagsMock = {
  data: {
    __typename: 'Query',
    client: {
      __typename: 'Client',
      id: '1234',
      featureFlags: [{
        __typename: 'FeatureFlag',
        name: 'client.feedback_thumbs_enabled',
        enabled: true
      }]
    }
  }
};
export { getFeatureFlagsApolloMock, getFeatureFlagsResponseMock } from '../../mocks/featureFlagsMock';
export type { FeatureFlagsResponse } from '../../mocks/featureFlagsMock';
export const getSimilarItemsMock = ({
  skuId
}: {
  skuId: string;
}): MockedResponse<GetSimilarItemsQuery> => ({
  request: {
    query: GetSimilarItemsDocument,
    variables: {
      skuId
    }
  },
  result: {
    data: {
      __typename: 'Query',
      shopContent: {
        __typename: 'ShopContent',
        productDetailsPage: {
          __typename: 'ShopProductDetailsPage',
          discoveredProductCollectionByTreatmentType: {
            __typename: 'ShopProductDiscoverSkusCollection',
            shopProducts: {
              __typename: 'ShopProductConnection',
              nodes: [{
                __typename: 'ShopProduct',
                id: 'sim-1',
                name: 'Similar Product',
                brand: {
                  __typename: 'Brand',
                  name: 'Brand'
                },
                variantBySkuId: {
                  __typename: 'ShopProductVariant',
                  id: 'sim-1',
                  sku: skuId,
                  url: 'https://www.stitchfix.com/product/similar',
                  price: {
                    __typename: 'Money',
                    value: 1000,
                    currency: 'USD'
                  },
                  image: {
                    __typename: 'ShopMedia',
                    baseUrl: 'https://cdn.example.com/',
                    mediaPath: '/image.jpg',
                    altText: 'Similar'
                  },
                  priceMarkdown: null,
                  promoDiscountOffer: null
                }
              }]
            }
          }
        }
      }
    }
  } as FetchResult<GetSimilarItemsQuery>
});